import React, { useCallback, useEffect, useMemo, type FC } from 'react'

import { type Getter, type Setter, useAtom } from 'jotai'
import {
  FormElement,
  Select,
  Flex,
  FlexItem,
  TooltipUtils,
  Link,
  LINK_VARIANT_EXTRALIGHT,
} from 'mailup-web-components'
import { useIntl } from 'react-intl'

import { MAX_SELECT_ITEMS } from '../../../config/config'

import useAsyncSelect from 'Hooks/useAsyncSelect'

import {
  fetchCategoriesByName,
  fetchCategoryById,
} from 'Pages/unifiedFiltersListPage/services/search'
import { useAtomCallback } from 'jotai/utils'
import { filterFormAtom } from '../state/state'
import {
  modifyFilterCallback,
  openCreateCategoryFormCallback,
} from '../callbacks/form'

const CreateCategory: FC = () => {
  const intl = useIntl()
  const openCategoryForm = useAtomCallback(openCreateCategoryFormCallback)
  return (
    <Flex justifyContent='end'>
      <Link
        variant={LINK_VARIANT_EXTRALIGHT}
        onClick={() => {
          openCategoryForm()
        }}
      >
        {intl.formatMessage({ id: 'Load.CombinedFilterForm.CreateCategory' })}
      </Link>
    </Flex>
  )
}

const FilterFormCategories: FC = () => {
  const intl = useIntl()
  useEffect(() => {
    TooltipUtils.rebuild()
  }, [])

  const [{ Category }] = useAtom(filterFormAtom)
  const handleChange = useAtomCallback(
    useCallback((get: Getter, set: Setter, catId: number) => {
      modifyFilterCallback(get, set, {
        Category: catId,
      })
    }, []),
  )

  const { options, loading, searching, handleSearch, hint, hintNoResults } =
    useAsyncSelect({
      key: 'filterCategories',
      value: Category,
      maxItems: MAX_SELECT_ITEMS,
      fetchItems: fetchCategoriesByName,
      fetchItemsByTerm: fetchCategoriesByName,
      fetchItemById: fetchCategoryById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const optionsWithNoCategory = useMemo(() => {
    return searching
      ? options
      : [
          {
            value: 0,
            label: intl.formatMessage({ id: 'Load.Filters.NoCategory' }),
          },
        ].concat(options)
  }, [searching, options, intl])

  const handleExpandCollapse = (c: boolean): void => {
    if (!c) {
      handleSearch('')
    }
  }

  return (
    <FlexItem flex='1 0 auto'>
      <FormElement
        label={intl.formatMessage({ id: 'Load.Common.Category' })}
        labelHelp={intl.formatMessage({
          id: 'Load.CombinedFilterForm.CategoryTooltip',
        })}
        LabelRightComponent={CreateCategory}
      >
        <Select
          data-mu-test='filterForm-category'
          value={Category}
          options={optionsWithNoCategory}
          loading={loading}
          onChange={handleChange}
          onSearch={handleSearch}
          onExpandCollapse={handleExpandCollapse}
          hint={hint}
          hintNoResults={hintNoResults}
        />
      </FormElement>
    </FlexItem>
  )
}

export default FilterFormCategories
